import { useFormContext, useWatch } from "react-hook-form";
import { Check, UserRound } from "lucide-react";
import { TRANSPORT_TYPE_LABEL } from "@/lib/tripLabels";
import {
  formatTimeForDisplay,
  joinAddressLine,
  needsAmPm,
  normalizeDateForPayload,
  normalizeTimeForPayload,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import { usePatientLookupState } from "./PatientLookupContext";
import type { TripFormValues } from "./schema";
import type { StepKey } from "./wizard";

// =============================================================================
// TripSummaryRail
//
// The right-hand column on wide screens: a read-only summary of the trip
// that fills in as each step is answered, and the list of every step with
// where the booker is. It watches form state and never writes to it, so it
// cannot change what gets booked — the Review step remains the authoritative
// read-out before submit.
//
// Values are phrased in sentence case ("Round trip") and names are split
// across elements, so nothing here duplicates the exact text of the step
// cards on the left.
// =============================================================================

const TRIP_LABELS: Record<string, string> = {
  one_way: "One way",
  round_trip: "Round trip",
  multi_leg: "Multi-leg",
};

const STEP_TITLES: Record<StepKey, string> = {
  lookup: "Find patient",
  details: "Patient details",
  tripType: "Trip type",
  transportType: "Level of service",
  transportDate: "Date",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  return: "Return trip",
  multiLeg: "Extra legs",
  requester: "Requester",
  submit: "Review & submit",
};

interface TripSummaryRailProps {
  steps: StepKey[];
  currentStep: StepKey;
  answered: ReadonlySet<StepKey>;
}

export function TripSummaryRail({ steps, currentStep, answered }: TripSummaryRailProps) {
  const { control } = useFormContext<TripFormValues>();
  const v = useWatch({ control });
  const lookup = usePatientLookupState();

  const first = String(v.firstName ?? "").trim();
  const last = String(v.lastName ?? "").trim();
  const initials = `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
  const mrn = String(v.mrn ?? "").trim();

  const iso = normalizeDateForPayload(String(v.transportDate ?? ""));
  const dateLabel = iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "";
  const timeRaw = String(v.primaryTime ?? "").trim();
  const timeLabel = v.pickupWillCall
    ? "Will call"
    : timeRaw
      ? `${v.timeType === "appointment" ? "Appt" : "Pickup"} ${needsAmPm(timeRaw) ? timeRaw : formatTimeForDisplay(normalizeTimeForPayload(timeRaw))}`
      : "";
  const when = [dateLabel, timeLabel].filter(Boolean).join(" · ");

  const from = joinAddressLine(String(v.pickupAddress ?? ""), String(v.pickupCityZip ?? ""));
  const to = joinAddressLine(
    String(v.destinationAddress ?? ""),
    String(v.destinationCityZip ?? ""),
  );

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-2xl border border-border/70 bg-card/60 p-5 shadow-[0_24px_60px_-36px_hsl(222_90%_2%/0.9)] backdrop-blur-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold tracking-tight text-foreground">Trip summary</h2>
          <span className="inline-flex items-center gap-1.5 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" />
            Live
          </span>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/25 to-[hsl(var(--accent-2)/0.25)] text-sm font-bold text-foreground"
          >
            {initials || <UserRound className="h-4 w-4 text-muted-foreground" />}
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              {first || last ? (
                <>
                  <span>{first}</span> <span>{last}</span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  {!mrn
                    ? "No patient yet"
                    : lookup.status === "loading"
                      ? "Looking up…"
                      : lookup.status === "not-found"
                        ? "First-time patient"
                        : "Patient"}
                </span>
              )}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {mrn ? (
                <span className="font-mono">{mrn}</span>
              ) : (
                "Find them by MRN and date of birth"
              )}
            </p>
          </div>
        </div>

        <dl className="mt-4 grid gap-2.5 border-t border-border/60 pt-4 text-sm">
          <SummaryRow label="Trip" value={TRIP_LABELS[String(v.tripType ?? "")] ?? ""} />
          <SummaryRow
            label="Level"
            value={
              (TRANSPORT_TYPE_LABEL as Record<string, string>)[String(v.transportType ?? "")] ?? ""
            }
          />
          <SummaryRow label="When" value={when} />
        </dl>

        <div className="mt-4 border-t border-border/60 pt-4">
          <p className="text-[0.68rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            Route
          </p>
          <ol className="mt-2.5 grid gap-3">
            <RouteStop
              tone="from"
              label="Pick-up"
              place={String(v.pickupLocation ?? "")}
              value={from}
            />
            <RouteStop tone="to" label="Drop-off" value={to} />
          </ol>
        </div>
      </section>

      <section className="rounded-2xl border border-border/70 bg-card/40 p-5 backdrop-blur-xl">
        <h2 className="text-sm font-bold tracking-tight text-foreground">Steps</h2>
        <ol className="mt-3 grid gap-0.5">
          {steps.map((key, i) => {
            const state = key === currentStep ? "active" : answered.has(key) ? "done" : "todo";
            return (
              <li
                key={key}
                aria-current={state === "active" ? "step" : undefined}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors",
                  state === "active" && "bg-primary/[0.08]",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[0.7rem] font-bold",
                    state === "done" && "bg-success/15 text-success",
                    state === "active" &&
                      "bg-gradient-to-br from-primary to-[hsl(var(--accent-2))] text-primary-foreground",
                    state === "todo" && "border border-border text-muted-foreground",
                  )}
                >
                  {state === "done" ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
                </span>
                <span
                  className={cn(
                    "text-sm",
                    state === "active" && "font-semibold text-foreground",
                    state === "done" && "text-foreground/80",
                    state === "todo" && "text-muted-foreground",
                  )}
                >
                  {STEP_TITLES[key]}
                </span>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "truncate text-right",
          value ? "font-semibold text-foreground" : "text-muted-foreground/60",
        )}
      >
        {value || "—"}
      </dd>
    </div>
  );
}

function RouteStop({
  tone,
  label,
  place,
  value,
}: {
  tone: "from" | "to";
  label: string;
  place?: string;
  value: string;
}) {
  return (
    <li className="relative flex gap-3">
      <span
        aria-hidden="true"
        className={cn(
          "relative z-10 mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ring-4 ring-card",
          tone === "from" ? "bg-primary" : "bg-success",
        )}
      />
      {tone === "from" ? (
        <span
          aria-hidden="true"
          className="absolute left-[4.5px] top-3.5 h-[calc(100%+0.25rem)] w-px bg-border"
        />
      ) : null}
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{place || label}</p>
        <p
          className={cn(
            "text-sm leading-snug",
            value ? "text-foreground" : "text-muted-foreground/60",
          )}
        >
          {value || "Not set yet"}
        </p>
      </div>
    </li>
  );
}
